import { CustomerPortalLayout } from "@customer-gallery/components/CustomerPortalLayout";
import { customerRoutePush, customerRouteReplace } from "@customer-gallery/definitions/routeDefinitions";
import { loadUser } from "@customer-gallery/redux/actionCreators/auth";
import { loadUnclaimedTokens } from "@customer-gallery/redux/actionCreators/unclaimedTokens";
import { useNovelCustomerDispatch, useNovelCustomerSelector } from "@customer-gallery/redux/reduxHooks";
import { loadNovelIntercom } from "@novel/shared/utils/loadNovelIntercom";
import * as Sentry from "@sentry/browser";
import { Frame } from "@shopify/polaris";
import { useIsSuperUserSelector } from "@customer-gallery/redux/reducers/auth";
import { useRouter } from "next/router";
import React, { ReactNode, useEffect } from "react";
import { SkeletonLoadingBackground } from "@customer-gallery/components/SkeletonLoadingBackground";
interface Props {
  readonly children?: ReactNode;
  readonly isSuperUserRoute?: boolean;
  readonly isPublicRoute?: boolean;
  readonly isLoggedOutRoute?: boolean;
}
export function AuthContainer({
  children,
  isSuperUserRoute,
  isPublicRoute,
  isLoggedOutRoute
}: Props): JSX.Element {
  const isSuperUser = useIsSuperUserSelector();
  const user = useNovelCustomerSelector(state => state.auth.user);
  const userIsLoading = useNovelCustomerSelector(state => state.auth.isLoadingUser);
  const unclaimedTokensIsLoading = useNovelCustomerSelector(state => state.unclaimedTokens.isLoading);
  const dispatch = useNovelCustomerDispatch();
  const router = useRouter();
  const userNotAuthorized = isSuperUserRoute && user && !user.isSuperUser;
  // load user if not loaded
  useEffect(() => {
    if (!user && userIsLoading) {
      dispatch(loadUser());
    }
  }, [dispatch, user, userIsLoading]);

  // load unclaimed tokens once user is loaded
  useEffect(() => {
    if (user?.primaryEmail) {
      dispatch(loadUnclaimedTokens());
      loadNovelIntercom({
        email: user.primaryEmail,
        phoneNumber: user.mostRecentPhoneNumber,
        firstName: user?.firstName,
        lastName: user?.lastName,
        userName: user?.displayName
      });
    }
  }, [dispatch, user]);

  // Redirect to login page on authed routes if no user
  useEffect(() => {
    const userNotLoggedIn = !user && !userIsLoading;
    if (userNotLoggedIn) {
      if (!isPublicRoute && !isLoggedOutRoute) {
        customerRoutePush("/login");
      }
    } else {
      if (user && isLoggedOutRoute) {
        customerRoutePush("/");
      }
    }
  }, [user, userIsLoading, router.pathname, isSuperUserRoute, isPublicRoute, isLoggedOutRoute]);
  useEffect(() => {
    // if (!isSuperUser) {
    //     if (user) {
    //         const {
    //             firstName,
    //             lastName,
    //             displayName,
    //             primaryEmail,

    //             // removing these from fullstory
    //             cryptoAddresses,
    //             orgCustomerRelationships,
    //             isSuperUser,
    //             socialAccounts,

    //             ...otherFields
    //         } = user;
    //         setUpFullStory("gallery", user.id, {
    //             displayName: displayName || [firstName, lastName].filter(Boolean).join(" "),
    //             email: primaryEmail,
    //             ...otherFields,
    //         });
    //     } else {
    //         setUpFullStory("gallery");
    //     }
    // }

    if (user) {
      // If user is valid we should track him using this id and email, to track specific issues
      Sentry.setUser({
        id: user.id,
        email: user.primaryEmail
      });
    } else {
      // If user is not valid, we should remove any user information
      Sentry.configureScope(scope => scope.setUser(null));
    }
  }, [isSuperUser, user]);
  if (isPublicRoute) {
    return <React.Fragment>{children}</React.Fragment>;
  }
  if (userIsLoading || user && unclaimedTokensIsLoading) {
    return <SkeletonLoadingBackground />;
  }
  if (userNotAuthorized) {
    customerRouteReplace("/login");
    return <SkeletonLoadingBackground />;
  }
  if (!user) {
    return <Frame>{children}</Frame>;
  }
  return <CustomerPortalLayout>{children}</CustomerPortalLayout>;
}
AuthContainer.displayName = "AuthContainer";